import React, { useMemo } from 'react';
import { MenuItem, Select, TextField } from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import moment from 'moment/moment';
import { UseDefaultRowConfigProps } from './useDefaultRowConfig.props';
import { BulkBacksolveUpdate, BulkValuationUpdate } from '../../../../../../api';
import { dbShortDate, ISO_DATE_FORMAT } from '../../../../../../utilities';
import { BulkActionsColumn } from '../../BulkActionsDrawer.types';
import {
  ADJUSTMENT_SELECTED_FIELD,
  ADJUSTMENT_TYPE_SELECTION_FIELD,
  AS_OF_DATE_FIELD,
  BACKSOLVE_FIELD,
  IS_FIRM_OWNED_FIELD,
  IS_READY_FOR_AUDIT_FIELD,
  MARKET_DATE_FIELD,
  MAX_PERCENT_CHANGE_FIELD,
  METRIC_FIELD,
  MIN_PERCENT_CHANGE_FIELD, OPM_BACKSOLVE_DATE_FIELD,
  STATUS
} from '../../constants';

const useDefaultRowConfig = ({
  defaultValues,
  visibleColumns,
  latestPossibleMarketDate,
  handleDefaultValueChange,
  isScalarUser,
}: UseDefaultRowConfigProps) => {
  
  const defaultRowConfig = useMemo<BulkActionsColumn[]>(
    () => [
      {
        renderCell: () => <span>Default Values</span>,
        isVisible: true,
      },
      {
        field: MARKET_DATE_FIELD,
        renderCell: () => (
          <DatePicker
            slotProps={{
              textField: {
                variant: 'standard',
              },
            }}
            value={defaultValues.market_date ? moment(defaultValues.market_date, ISO_DATE_FORMAT).toDate() : null}
            format="MM/dd/yyyy"
            views={['year', 'month', 'day']}
            maxDate={latestPossibleMarketDate.toDate()}
            onChange={date => {
              const formattedDate = date ? dbShortDate(date) : '';
              handleDefaultValueChange(MARKET_DATE_FIELD, formattedDate);
            }}
          />
        ),
        isVisible: visibleColumns.includes(MARKET_DATE_FIELD),
      },
      {
        field: STATUS,
        renderCell: () => (
          <Select
            variant="standard"
            value={defaultValues.status}
            labelId="status-select-header-label"
            id="status-select-header"
            onChange={event => {
              handleDefaultValueChange(STATUS, event.target.value);
              if (event.target.value === BulkValuationUpdate.status.DRAFT) {
                handleDefaultValueChange(IS_READY_FOR_AUDIT_FIELD, false);
              }
            }}>
            {Object.values(BulkValuationUpdate.status).map(status => (
              <MenuItem key={status} value={status}>
                {status}
              </MenuItem>
            ))}
          </Select>
        ),
        isVisible: visibleColumns.includes(STATUS),
      },
      {
        field: IS_FIRM_OWNED_FIELD,
        renderCell: () => (
          <Select
            variant="standard"
            value={Number(defaultValues.is_firm_owned)}
            labelId="owner-select-header-label"
            id="owner-select-header"
            disabled={!isScalarUser}
            onChange={event => handleDefaultValueChange(IS_FIRM_OWNED_FIELD, !!event.target.value)}>
            <MenuItem key="firm-valuation" value={1}>
              Firm Valuation
            </MenuItem>
            <MenuItem key="scalar-valuation" value={0}>
              Scalar Valuation
            </MenuItem>
          </Select>
        ),
        isVisible: visibleColumns.includes(IS_FIRM_OWNED_FIELD),
      },
      {
        field: IS_READY_FOR_AUDIT_FIELD,
        renderCell: () => (
          <Select
            variant="standard"
            value={Number(defaultValues.is_ready_for_audit)}
            labelId="audit-select-header-label"
            id="audit-select-header"
            onChange={event => handleDefaultValueChange(IS_READY_FOR_AUDIT_FIELD, !!event.target.value)}>
            <MenuItem key="ready-for-audit" value={1}>
              Ready for Audit
            </MenuItem>
            <MenuItem key="not-ready-for-audit" value={0}>
              Not Ready for Audit
            </MenuItem>
          </Select>
        ),
        isVisible: visibleColumns.includes(IS_READY_FOR_AUDIT_FIELD),
      },
      {
        field: AS_OF_DATE_FIELD,
        renderCell: () => (
          <DatePicker
            value={defaultValues.as_of_date ? moment(defaultValues.as_of_date, ISO_DATE_FORMAT).toDate() : null}
            format="MM/dd/yyyy"
            views={['year', 'month', 'day']}
            maxDate={latestPossibleMarketDate.toDate()}
            onChange={(date: Date | null) => {
              const formattedDate = date ? dbShortDate(date) : '';
              handleDefaultValueChange(AS_OF_DATE_FIELD, formattedDate);
            }}
            slotProps={{
              textField: {
                variant: 'standard',
              },
            }}
          />
        ),
        isVisible: visibleColumns.includes(BACKSOLVE_FIELD),
      },
      {
        field: OPM_BACKSOLVE_DATE_FIELD,
        renderCell: () => (
          <DatePicker
            value={defaultValues.as_of_date ? moment(defaultValues.as_of_date, ISO_DATE_FORMAT).toDate() : null}
            format="MM/dd/yyyy"
            views={['year', 'month', 'day']}
            maxDate={latestPossibleMarketDate.toDate()}
            onChange={date => {
              const formattedDate = date ? dbShortDate(date) : '';
              handleDefaultValueChange(OPM_BACKSOLVE_DATE_FIELD, formattedDate);
            }}
            slotProps={{
              textField: {
                variant: 'standard',
              },
            }}
          />
        ),
        isVisible: visibleColumns.includes(BACKSOLVE_FIELD),
      },
      {
        field: ADJUSTMENT_TYPE_SELECTION_FIELD,
        isVisible: visibleColumns.includes(BACKSOLVE_FIELD),
        renderCell: () => (
          <Select
            value={defaultValues[ADJUSTMENT_TYPE_SELECTION_FIELD]}
            labelId="value-to-adjust-label"
            id="value-to-adjust"
            onChange={event => handleDefaultValueChange(ADJUSTMENT_TYPE_SELECTION_FIELD, event.target.value)}
            variant="standard">
            {Object.values(BulkBacksolveUpdate.adjustment_type_selection).map(adjustmentType => (
              <MenuItem key={adjustmentType} value={adjustmentType}>
                {adjustmentType}
              </MenuItem>
            ))}
          </Select>
        ),
      },
      {
        field: METRIC_FIELD,
        isVisible: visibleColumns.includes(BACKSOLVE_FIELD),
        renderCell: () => (
          <Select
            value={defaultValues[METRIC_FIELD]}
            labelId={`${METRIC_FIELD}-label`}
            id={METRIC_FIELD}
            onChange={event => handleDefaultValueChange(METRIC_FIELD, event.target.value)}
            variant="standard">
            {Object.values(BulkBacksolveUpdate.metric).map(value => (
              <MenuItem key={value} value={value}>
                {value}
              </MenuItem>
            ))}
          </Select>
        ),
      },
      {
        field: ADJUSTMENT_SELECTED_FIELD,
        isVisible: visibleColumns.includes(BACKSOLVE_FIELD),
        renderCell: () => (
          <Select
            value={defaultValues[ADJUSTMENT_SELECTED_FIELD]}
            labelId={`${ADJUSTMENT_SELECTED_FIELD}-label`}
            id={ADJUSTMENT_SELECTED_FIELD}
            onChange={event => {
              handleDefaultValueChange(ADJUSTMENT_SELECTED_FIELD, event.target.value);
            }}
            variant="standard">
            {Object.values(BulkBacksolveUpdate.adjustment_selected).map(percentileLabel => (
              <MenuItem key={percentileLabel} value={percentileLabel}>
                {percentileLabel}
              </MenuItem>
            ))}
          </Select>
        ),
      },
      {
        field: MIN_PERCENT_CHANGE_FIELD,
        isVisible: visibleColumns.includes(BACKSOLVE_FIELD),
        renderCell: () => (
          <TextField
            type="number"
            value={defaultValues[MIN_PERCENT_CHANGE_FIELD] || ''}
            onChange={event => handleDefaultValueChange(MIN_PERCENT_CHANGE_FIELD, event.target.value)}
            placeholder="Min Percent Change"
            variant="standard"
            size="small"
          />
        ),
      },
      {
        field: MAX_PERCENT_CHANGE_FIELD,
        isVisible: visibleColumns.includes(BACKSOLVE_FIELD),
        renderCell: () => (
          <TextField
            type="number"
            value={defaultValues[MAX_PERCENT_CHANGE_FIELD] || ''}
            onChange={event => handleDefaultValueChange(MAX_PERCENT_CHANGE_FIELD, event.target.value)}
            placeholder="Max Percent Change"
            variant="standard"
            size="small"
          />
        ),
      },
    ],
    [defaultValues, visibleColumns, latestPossibleMarketDate, handleDefaultValueChange, isScalarUser]
  );

  return defaultRowConfig;
};

export default useDefaultRowConfig;
